import React, { useState, useEffect } from 'react';
import {
  Settings, Plus, Trash2, CheckCircle, XCircle, Loader2,
  Eye, EyeOff, DollarSign, Shield,
} from 'lucide-react';
import * as svc from '../services/tradingService';
import { ExchangeCredential, ExchangeCredentialCreate, SupportedExchange } from '../types/trading';

// ─── helpers ────────────────────────────────────────────────────────────────

const EXCHANGE_ICONS: Record<string, string> = {
  kraken:   '🐙',
  binance:  '🟡',
  coinbase: '🔵',
  alpaca:   '🦙',
};

const EXCHANGE_COLORS: Record<string, string> = {
  crypto:    'from-orange-500/20 to-amber-500/10 border-orange-500/30',
  equities:  'from-blue-500/20 to-cyan-500/10 border-blue-500/30',
};

interface FormState {
  name: string;
  exchange_id: string;
  is_paper: boolean;
  api_key: string;
  api_secret: string;
  api_passphrase: string;
}

const EMPTY_FORM: FormState = {
  name: '', exchange_id: 'kraken', is_paper: true,
  api_key: '', api_secret: '', api_passphrase: '',
};

// ─── BalanceDisplay ──────────────────────────────────────────────────────────

const BalanceDisplay: React.FC<{ balance: any }> = ({ balance }) => {
  if (!balance) return null;
  if (balance.error) return (
    <p className="text-xs text-red-400 mt-2">Error: {balance.error}</p>
  );

  // Alpaca account
  if (balance.portfolio_value !== undefined) return (
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
      {[
        { label: 'Portfolio', value: `$${Number(balance.portfolio_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        { label: 'Cash', value: `$${Number(balance.cash).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        { label: 'Buying Power', value: `$${Number(balance.buying_power).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        { label: 'Mode', value: balance.is_paper ? 'Paper' : 'Live' },
      ].map(item => (
        <div key={item.label} className="bg-slate-800/60 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400">{item.label}</p>
          <p className="text-sm font-semibold text-white">{item.value}</p>
        </div>
      ))}
    </div>
  );

  // CCXT exchange
  const total = balance.total || {};
  const free  = balance.free  || {};
  const assets = Object.keys(total).filter(k => total[k] > 0);
  if (assets.length === 0) return <p className="text-xs text-slate-400 mt-2">No balances found.</p>;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {assets.map(asset => (
        <div key={asset} className="bg-slate-800/60 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400">{asset}</p>
          <p className="text-sm font-semibold text-white">{free[asset]?.toFixed(8) ?? '—'}</p>
          <p className="text-xs text-slate-500">Total: {total[asset]?.toFixed(8)}</p>
        </div>
      ))}
    </div>
  );
};

// ─── CredentialCard ──────────────────────────────────────────────────────────

const CredentialCard: React.FC<{
  cred: ExchangeCredential;
  exchangeMeta?: SupportedExchange;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  onBalance: (id: string) => void;
  testResult?: { success: boolean; message: string } | null;
  balance?: any;
  loadingTest?: boolean;
  loadingBalance?: boolean;
}> = ({ cred, exchangeMeta, onDelete, onTest, onBalance, testResult, balance, loadingTest, loadingBalance }) => {
  const [showBalance, setShowBalance] = useState(false);

  return (
    <div className={`bg-gradient-to-br ${EXCHANGE_COLORS[exchangeMeta?.type ?? 'crypto']} border rounded-xl p-4 space-y-3`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="text-2xl">{EXCHANGE_ICONS[cred.exchange_id] ?? '🔑'}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">{cred.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
              cred.is_paper
                ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                : 'bg-orange-500/20 text-orange-300 border-orange-500/30'
            }`}>
              {cred.is_paper ? 'Paper' : 'Live'}
            </span>
            {!cred.is_active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 border border-slate-600">Inactive</span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5 capitalize">{exchangeMeta?.label ?? cred.exchange_id} · {exchangeMeta?.type}</p>
        </div>
        {/* Connection status */}
        {cred.last_tested_at && (
          <div className="flex items-center gap-1.5">
            {cred.last_test_success
              ? <CheckCircle size={14} className="text-green-400" />
              : <XCircle    size={14} className="text-red-400" />
            }
            <span className={`text-xs ${cred.last_test_success ? 'text-green-400' : 'text-red-400'}`}>
              {cred.last_test_success ? 'Connected' : 'Failed'}
            </span>
          </div>
        )}
      </div>

      {/* Key presence */}
      <div className="flex gap-3 text-xs text-slate-400">
        <span className={cred.has_api_key    ? 'text-green-400' : 'text-slate-600'}>
          {cred.has_api_key    ? '✓' : '✗'} API Key
        </span>
        <span className={cred.has_api_secret ? 'text-green-400' : 'text-slate-600'}>
          {cred.has_api_secret ? '✓' : '✗'} API Secret
        </span>
        {cred.last_tested_at && (
          <span className="ml-auto text-slate-500">
            Last tested: {new Date(cred.last_tested_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Error message */}
      {testResult && !testResult.success && (
        <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-3 py-2">
          {testResult.message}
        </p>
      )}
      {testResult?.success && (
        <p className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 rounded px-3 py-2">
          {testResult.message}
        </p>
      )}

      {/* Balance */}
      {showBalance && <BalanceDisplay balance={balance} />}

      {/* Action buttons */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onTest(cred.id)}
          disabled={loadingTest}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg border border-slate-600 transition disabled:opacity-50"
        >
          {loadingTest ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
          Test
        </button>
        <button
          onClick={() => { onBalance(cred.id); setShowBalance(true); }}
          disabled={loadingBalance}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg border border-slate-600 transition disabled:opacity-50"
        >
          {loadingBalance ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />}
          Balance
        </button>
        <button
          onClick={() => onDelete(cred.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg border border-red-500/30 transition ml-auto"
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const ExchangeSettingsPage: React.FC = () => {
  const [credentials, setCredentials] = useState<ExchangeCredential[]>([]);
  const [supported, setSupported]     = useState<SupportedExchange[]>([]);
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState<FormState>(EMPTY_FORM);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  // Per-credential state
  const [testResults, setTestResults]       = useState<Record<string, { success: boolean; message: string }>>({});
  const [balances, setBalances]             = useState<Record<string, any>>({});
  const [loadingTest, setLoadingTest]       = useState<Record<string, boolean>>({});
  const [loadingBalance, setLoadingBalance] = useState<Record<string, boolean>>({});

  // Load data
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [creds, sup] = await Promise.all([
        svc.listExchangeCredentials(),
        svc.listSupportedExchanges(),
      ]);
      setCredentials(creds);
      setSupported(sup);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.api_key || !form.api_secret) {
      setError('Name, API Key, and API Secret are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body: ExchangeCredentialCreate = {
        name: form.name,
        exchange_id: form.exchange_id,
        is_paper: form.is_paper,
        api_key: form.api_key,
        api_secret: form.api_secret,
        api_passphrase: form.api_passphrase || undefined,
      };
      await svc.createExchangeCredential(body);
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this exchange credential?')) return;
    await svc.deleteExchangeCredential(id);
    await loadAll();
  };

  const handleTest = async (id: string) => {
    setLoadingTest(prev => ({ ...prev, [id]: true }));
    try {
      const result = await svc.testExchangeCredential(id);
      setTestResults(prev => ({ ...prev, [id]: result }));
      await loadAll(); // refresh last_test_success
    } catch (e) {
      setTestResults(prev => ({ ...prev, [id]: { success: false, message: 'Request failed' } }));
    } finally {
      setLoadingTest(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleBalance = async (id: string) => {
    setLoadingBalance(prev => ({ ...prev, [id]: true }));
    try {
      const bal = await svc.getExchangeBalance(id);
      setBalances(prev => ({ ...prev, [id]: bal }));
    } catch (e) {
      setBalances(prev => ({ ...prev, [id]: { error: 'Failed to fetch balance' } }));
    } finally {
      setLoadingBalance(prev => ({ ...prev, [id]: false }));
    }
  };

  const cryptoCreds   = credentials.filter(c => supported.find(s => s.exchange_id === c.exchange_id)?.type === 'crypto');
  const equityCreds   = credentials.filter(c => supported.find(s => s.exchange_id === c.exchange_id)?.type === 'equities');
  const getSupported = (id: string) => supported.find(s => s.exchange_id === id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings size={24} className="text-blue-400" />
            Exchange Settings
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage API credentials for crypto and equities exchanges.
            Keys are encrypted at rest.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition"
        >
          <Plus size={16} />
          Add Exchange
        </button>
      </div>

      {/* Security note */}
      <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3">
        <Shield size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-300">
          All API keys and secrets are encrypted using AES-256 (Fernet) before storage.
          Keys are never transmitted or logged in plaintext.
          Use <strong>Paper Trading</strong> mode while testing to avoid executing real trades.
        </p>
      </div>

      {/* Add credential form */}
      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white">Add Exchange Credential</h3>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 text-sm text-red-400">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Display Name</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Kraken Main, Alpaca Paper"
                className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Exchange</label>
              <select
                value={form.exchange_id}
                onChange={e => setForm(f => ({ ...f, exchange_id: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2"
              >
                {supported.map(s => (
                  <option key={s.exchange_id} value={s.exchange_id}>
                    {EXCHANGE_ICONS[s.exchange_id]} {s.label} ({s.type})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Paper / Live toggle */}
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400">Trading Mode:</span>
            <div className="flex rounded-lg overflow-hidden border border-slate-600">
              {[
                { label: '📄 Paper Trading', val: true  },
                { label: '⚡ Live Trading',  val: false },
              ].map(({ label, val }) => (
                <button
                  key={label}
                  onClick={() => setForm(f => ({ ...f, is_paper: val }))}
                  className={`px-4 py-1.5 text-xs font-medium transition ${
                    form.is_paper === val ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {!form.is_paper && (
              <span className="text-xs text-orange-400 bg-orange-400/10 border border-orange-400/20 rounded px-2 py-1">
                ⚠ Live mode will execute real trades with real funds
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">API Key</label>
              <div className="relative">
                <input
                  value={form.api_key}
                  onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                  type={showSecrets['api_key'] ? 'text' : 'password'}
                  placeholder="Your API key"
                  className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 pr-10 font-mono focus:border-blue-500 outline-none"
                />
                <button
                  onClick={() => setShowSecrets(s => ({ ...s, api_key: !s['api_key'] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showSecrets['api_key'] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">API Secret</label>
              <div className="relative">
                <input
                  value={form.api_secret}
                  onChange={e => setForm(f => ({ ...f, api_secret: e.target.value }))}
                  type={showSecrets['api_secret'] ? 'text' : 'password'}
                  placeholder="Your API secret"
                  className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 pr-10 font-mono focus:border-blue-500 outline-none"
                />
                <button
                  onClick={() => setShowSecrets(s => ({ ...s, api_secret: !s['api_secret'] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showSecrets['api_secret'] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            {(form.exchange_id === 'coinbase' || form.exchange_id === 'okx') && (
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1">Passphrase (optional)</label>
                <input
                  value={form.api_passphrase}
                  onChange={e => setForm(f => ({ ...f, api_passphrase: e.target.value }))}
                  type="password"
                  placeholder="API passphrase"
                  className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 font-mono focus:border-blue-500 outline-none"
                />
              </div>
            )}
          </div>

          {/* Alpaca help text */}
          {form.exchange_id === 'alpaca' && (
            <div className="bg-slate-700/50 rounded-lg px-4 py-3 text-xs text-slate-300 space-y-1">
              <p className="font-medium text-white">🦙 Alpaca Setup</p>
              <p>1. Sign up at <span className="text-blue-400">alpaca.markets</span></p>
              <p>2. Go to <strong>Paper Trading</strong> in the dashboard to get paper API keys</p>
              <p>3. For live trading, complete identity verification and fund your account</p>
              <p>4. Paper keys only work with "Paper Trading" mode enabled above</p>
            </div>
          )}

          {form.exchange_id === 'kraken' && (
            <div className="bg-slate-700/50 rounded-lg px-4 py-3 text-xs text-slate-300 space-y-1">
              <p className="font-medium text-white">🐙 Kraken Setup</p>
              <p>1. Log in to Kraken → Security → API</p>
              <p>2. Create a key with: <strong>Query Funds</strong>, <strong>Create &amp; Modify Orders</strong></p>
              <p>3. For USDC paper trading, Kraken does not have a native paper mode —
                 enable "Paper" mode in LiquidBot to simulate trades locally</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Save Credential
            </button>
            <button
              onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setError(''); }}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg border border-slate-600 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Crypto exchanges */}
      <section>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="text-lg">₿</span> Crypto Exchanges
        </h2>
        {cryptoCreds.length === 0 ? (
          <div className="border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-500 text-sm">
            No crypto exchange credentials configured.
            <br />Add Kraken, Binance, or Coinbase credentials to start trading.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {cryptoCreds.map(cred => (
              <CredentialCard
                key={cred.id}
                cred={cred}
                exchangeMeta={getSupported(cred.exchange_id)}
                onDelete={handleDelete}
                onTest={handleTest}
                onBalance={handleBalance}
                testResult={testResults[cred.id]}
                balance={balances[cred.id]}
                loadingTest={loadingTest[cred.id]}
                loadingBalance={loadingBalance[cred.id]}
              />
            ))}
          </div>
        )}
      </section>

      {/* Equities exchanges */}
      <section>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="text-lg">📈</span> Equities Exchanges
        </h2>
        {equityCreds.length === 0 ? (
          <div className="border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-500 text-sm">
            No equities exchange credentials configured.
            <br />Add Alpaca credentials to enable stock and ETF trading.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {equityCreds.map(cred => (
              <CredentialCard
                key={cred.id}
                cred={cred}
                exchangeMeta={getSupported(cred.exchange_id)}
                onDelete={handleDelete}
                onTest={handleTest}
                onBalance={handleBalance}
                testResult={testResults[cred.id]}
                balance={balances[cred.id]}
                loadingTest={loadingTest[cred.id]}
                loadingBalance={loadingBalance[cred.id]}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick reference */}
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          {
            title: '🐙 Kraken (Crypto)',
            lines: [
              'Supports: BTC, ETH, SOL, and 200+ crypto pairs',
              'Paper mode: trades simulated locally by LiquidBot',
              'Live mode: requires funded USDC or fiat Kraken account',
              'Min order: varies by pair (~$10 equivalent)',
            ],
          },
          {
            title: '🦙 Alpaca (Equities)',
            lines: [
              'Supports: US stocks, ETFs, and crypto via Alpaca',
              'Paper mode: $100,000 virtual account, free',
              'Live mode: commission-free US equities trading',
              'Fractional shares supported',
            ],
          },
        ].map(card => (
          <div key={card.title} className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-white mb-2">{card.title}</h3>
            <ul className="space-y-1">
              {card.lines.map((l, i) => (
                <li key={i} className="text-xs text-slate-400 flex items-start gap-2">
                  <span className="text-slate-600 mt-0.5">·</span>
                  {l}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExchangeSettingsPage;
